import { makeAutoObservable } from 'mobx';
import api from '../config/api';
import { Application, ApplicationsResponse, Statistics } from '../types';

export class ApplicationStore {
    applications: Application[] = [];
    statistics: Statistics | null = null;
    todayStatistics: Statistics | null = null;
    loading = false;
    error: string | null = null;
    total = 0;
    page = 1;
    limit = 20;

    constructor() {
        makeAutoObservable(this);
    }

    async fetchApplications(
        page = 1,
        limit = 20,
        status?: string,
        type?: string,
        startDate?: string,
        endDate?: string,
        formId?: string
    ) {
        this.loading = true;
        this.error = null;
        this.page = page;
        this.limit = limit;

        try {
            const params: any = { page, limit };
            if (status) params.status = status;
            if (type) params.type = type;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (formId) params.form_id = formId;

            const response = await api.get<ApplicationsResponse>('/api/applications', { params });
            this.applications = response.data.applications;
            this.total = response.data.total;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch applications';
            this.applications = [];
        } finally {
            this.loading = false;
        }
    }

    async fetchStatistics(startDate?: string, endDate?: string) {
        this.loading = true;
        this.error = null;

        try {
            const params: any = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await api.get<Statistics>('/api/statistics', { params });
            this.statistics = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch statistics';
            this.statistics = null;
        } finally {
            this.loading = false;
        }
    }

    async fetchTodayStatistics() {
        this.loading = true;
        this.error = null;

        try {
            const response = await api.get<Statistics>('/api/today-statistics');
            this.todayStatistics = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch today statistics';
            this.todayStatistics = null;
        } finally {
            this.loading = false;
        }
    }

    async resendVerificationEmail(applicationId: number): Promise<boolean> {
        try {
            await api.post(`/api/applications/${applicationId}/resend-verification`);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to resend verification email';
            return false;
        }
    }

    get applicationsByStatus() {
        const statusGroups: { [key: string]: Application[] } = {};

        this.applications.forEach(app => {
            if (!statusGroups[app.status]) {
                statusGroups[app.status] = [];
            }
            statusGroups[app.status].push(app);
        });

        return statusGroups;
    }

    get applicationsByType() {
        const typeGroups: { [key: string]: Application[] } = {};

        this.applications.forEach(app => {
            if (!typeGroups[app.type]) {
                typeGroups[app.type] = [];
            }
            typeGroups[app.type].push(app);
        });

        return typeGroups;
    }

    get recentApplications() {
        return this.applications
            .slice()
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 5);
    }

    get pendingVerificationApplications() {
        return this.applications.filter(app => !app.email_verified && app.status === 'pending');
    }

    get processingApplications() {
        return this.applications.filter(app => app.status === 'processing');
    }

    get completedApplications() {
        return this.applications.filter(app => app.status === 'completed');
    }

    get failedApplications() {
        return this.applications.filter(app => app.status === 'failed');
    }

    getApplicationById(id: number): Application | undefined {
        return this.applications.find(app => app.id === id);
    }

    getApplicationsByFormId(formId: string): Application[] {
        return this.applications.filter(app => app.form_id === formId);
    }

    getApplicationsByDateRange(startDate: Date, endDate: Date): Application[] {
        return this.applications.filter(app => {
            const appDate = new Date(app.created_at);
            return appDate >= startDate && appDate <= endDate;
        });
    }

    get conversionRate(): number {
        if (!this.statistics || this.statistics.total_applications === 0) return 0;
        return (this.statistics.completed_applications / this.statistics.total_applications) * 100;
    }

    get emailVerificationRate(): number {
        if (!this.statistics || this.statistics.total_applications === 0) return 0;
        return (this.statistics.email_verified / this.statistics.total_applications) * 100;
    }

    get crmSyncRate(): number {
        if (!this.statistics || this.statistics.email_verified === 0) return 0;
        return (this.statistics.sent_to_crm / this.statistics.email_verified) * 100;
    }

    resetError() {
        this.error = null;
    }

    clearApplications() {
        this.applications = [];
        this.total = 0;
        this.page = 1;
    }

    clearStatistics() {
        this.statistics = null;
        this.todayStatistics = null;
    }
}